// Mirrors FleetHd.Issue DispositionDto. Present once the issue is Closed.
// `AutoClosed` is system-only and must never be sent by the UI.

export const DispositionTypes = {
  RealAndFixed: 'RealAndFixed',
  FalsePositive: 'FalsePositive',
  Dismissed: 'Dismissed',
  Duplicate: 'Duplicate',
  AutoClosed: 'AutoClosed',
} as const;
export type DispositionType = typeof DispositionTypes[keyof typeof DispositionTypes];

// The four operator-requestable types (AutoClosed excluded).
export type OperatorDispositionType = Exclude<DispositionType, 'AutoClosed'>;

// How long the suppress verdict keeps the same problem off the screen. The writer honours
// this window: a re-fire inside it is recorded on the closed Issue and neither reopens it
// nor raises a new one, and past it the next signal opens a fresh Issue. A new operator
// report overrides the window, because a person asserting the problem again outranks it.
//
// Sent only with Dismissed. Without a window the verdict closes the Issue and the next
// signal opens another, which is what False positive already does.
export const SUPPRESSION_DAYS = 30;

// The bounds the reader may pick between. Nothing else in the stack bounds this: the API
// takes the number unchecked, and the writer adds it to a date on the read path rather
// than at close. An unbounded value is therefore written quietly and then throws on every
// later signal for that asset and problem. The ceiling also keeps the promise that a real
// problem comes back within a quarter; longer than that reads as never.
export const SUPPRESSION_MIN_DAYS = 1;
export const SUPPRESSION_MAX_DAYS = 90;

export type DispositionSource = 'Operator' | 'System' | 'External';

export interface DispositionDto {
  type: DispositionType | '';
  source: DispositionSource | '';
  externalSubject: string;
  cause: string;
  suppressionDurationDays: number;
  dispositionedAt: string;
  dispositionedBy: string;
  vehicleStateAtCloseOdometer: number;
  vehicleStateAtCloseEngineHours: number;
  vehicleStateAtCloseCapturedAt: string;
  duplicateOfIssueId: string;
}

// POST .../issues/{id}/disposition. Server fills dispositionedBy + timestamp.
export interface DispositionRequest {
  dispositionType: OperatorDispositionType;
  cause?: string;
  duplicateOfIssueId?: string;   // required iff dispositionType === 'Duplicate'
  suppressionDurationDays?: number;
}
